import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion } from "motion/react";
import { X } from "lucide-react";

import {
  Dialog,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { TooltipProvider } from "@/modern/components/ui/tooltip";
import { Badge } from "@/modern/components/ui/badge";
import { SURFACE_ICON_BTN_CLS } from "@/react/components/entityViews/files/constants/surfaceChrome";
import { cn } from "@/modern/utils";
import BryntumContext from "@/react/context/bryntumContext";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { EntityRecord } from "@/types/entity";
import ImageAnnotationEditor, {
  type ImageAnnotationEditorHandle,
} from "@/react/components/entityViews/files/components/editors/ImageAnnotationEditor";
import { queryClient } from "@/lib/queryClient";
import { fileKeys } from "@/react/components/entityViews/files/api/files.query-keys";
import { FileActionsMenu } from "@/react/components/entityViews/files/components/FileActionsMenu";
import { FileCollectionNavButtons } from "@/react/components/entityViews/files/components/FileCollectionNavButtons";
import { copyImageToClipboard } from "@/react/components/entityViews/files/utils/copyImageToClipboard";
import { DialogGuideBlocker } from "@/react/guides";
import { AnalyzeFileShell, AnalyzePanelContent } from "../AnalyzeFile/AnalyzeDialog";
import { AnalyzeFloatingPanel } from "../AnalyzeFile/components/AnalyzeFloatingPanel";
import { FileDetailModalProvider, useFileDetailModal } from "./context/FileDetailModalContext";
import { ImageViewer } from "./components/ImageViewer";
import { FileDetailRail } from "./components/FileDetailRail";
import { PromoteAiInsightChrome } from "./components/promote/components/AiInsightOverlay";
import { clearPromoteResumeStep } from "./components/promote/promoteResumeStorage";
import { useFocusedNavigationContainer } from "./hooks/useFileNavigation";
import type { FileViewerMode } from "./types";

const MotionDialogContent = motion.create(DialogPrimitive.Content);

/** Shared spring — width + height + radius run together for a scale-from-center morph. */
const SHELL_SPRING = { type: "spring" as const, stiffness: 420, damping: 38, mass: 0.8 };

const HEADER_CHROME_PX = 112; // title block + body padding (~7rem)

function readDialogZoom(): number {
  if (typeof window === "undefined") return 1;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--swivu-dialog-zoom")
    .trim();
  const zoom = Number.parseFloat(raw || "1");
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

function readShellTargets(viewportW: number, viewportH: number) {
  // Divide by --swivu-dialog-zoom (War Room sets 1.15) so visual size stays in viewport.
  const zoom = readDialogZoom();
  const vw = viewportW / zoom;
  const vh = viewportH / zoom;
  const compact = {
    width: Math.min(1440, vw * 0.96),
    height: Math.min(vh * 0.74 + HEADER_CHROME_PX, 780 + HEADER_CHROME_PX, vh * 0.94),
    borderRadius: 32,
  };
  const focused = {
    width: Math.max(vw - 24, 320),
    height: Math.max(vh - 24, 240),
    borderRadius: 16,
  };
  return { compact, focused };
}

function useShellTargets() {
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1440,
    h: typeof window !== "undefined" ? window.innerHeight : 900,
  }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return useMemo(() => readShellTargets(viewport.w, viewport.h), [viewport.w, viewport.h]);
}
const ENTITY_RECORD_NAME_MAP: Record<string, string> = {
  taskId: "Task",
  resourceId: "Resource",
  clientId: "Client",
};

interface FileDetailModalProps {
  entityRecord: EntityRecord;
  file: SwivuFile | null;
  navigationFiles?: SwivuFile[];
  open: boolean;
  setOpen: (open: boolean) => void;
  autoOpenPromotePanel?: boolean;
  initialViewerMode?: FileViewerMode;
}

/** Rail content mode — annotate / detached analyze fall back to Details. */
function resolveRailMode(
  viewerMode: FileViewerMode,
  isAnalyzePanelDetached: boolean,
): Exclude<FileViewerMode, "annotate"> {
  if (viewerMode === "annotate") return "default";
  if (viewerMode === "analyze" && isAnalyzePanelDetached) return "default";
  if (
    viewerMode === "default" ||
    viewerMode === "promote" ||
    viewerMode === "extract" ||
    viewerMode === "summaryShare" ||
    viewerMode === "analyze" ||
    viewerMode === "adjustPoints"
  ) {
    return viewerMode;
  }
  return "default";
}

const FileDetailModalContent = () => {
  const {
    file,
    open,
    setOpen,
    imageFile,
    isAnnotating,
    setIsAnnotating,
    project,
    entityRecord,
    hasPrevious,
    hasNext,
    handlePrevious,
    handleNext,
    isAnalyzeDialogOpen,
    handleCloseAnalyzePanel,
    handleFileOpen,
    isAnalyzePanelDetached,
    toggleAnalyzePanelDetach,
    files,
    handleAnnotating,
    handleOpenAnalyzePanel,
    handleOpenPromotePanel,
    handleOpenAdjustPointsPanel,
    handleOpenSummarySharePanel,
    handleCloseSummarySharePanel,
    setIsExtractionPanelOpen,
    handleFileDownload,
    deleteFile,
    shouldShowAdjustPoints,
    viewerMode,
    setViewerMode,
    isFocusedWorkspace,
  } = useFileDetailModal();

  const handleDeleteFile = useCallback(
    (id: number) => {
      clearPromoteResumeStep(id);
      void deleteFile(id).then(() => setOpen(false));
    },
    [deleteFile, setOpen],
  );

  const [analyzeDockSlot, setAnalyzeDockSlot] = useState<HTMLDivElement | null>(null);
  const annotationEditorRef = useRef<ImageAnnotationEditorHandle>(null);
  const analyzeDockRef = useCallback((node: HTMLDivElement | null) => {
    setAnalyzeDockSlot(node);
  }, []);

  const { focusContainer, focusContainerProps } = useFocusedNavigationContainer(
    open && !!file,
    file?.id,
  );

  const shellTargets = useShellTargets();
  const shellAnimate = isFocusedWorkspace ? shellTargets.focused : shellTargets.compact;

  const railMode = resolveRailMode(viewerMode, isAnalyzePanelDetached);
  const entityTypeLabel =
    ENTITY_RECORD_NAME_MAP[entityRecord.entityType] ?? "Entity";
  const manageTitle = file?.fileType === "image" ? "Manage media" : "Manage document";
  const headerTitle = isAnnotating ? "Annotating media" : manageTitle;
  const dialogZ = "z-[calc(var(--swivu-bryntum-z-index)+10)]";

  const handleRequestClose = useCallback(() => {
    if (isAnnotating) {
      annotationEditorRef.current?.requestClose();
      return;
    }
    setOpen(false);
  }, [isAnnotating, setOpen]);

  useEffect(() => {
    if (file && file.canViewGrant === 0) {
      setOpen(false);
    }
  }, [file, setOpen]);

  if (!open || !file) return null;

  return (
    <>

      <Dialog
        open={open}
        modal
        onOpenChange={(next) => {
          if (!next) handleRequestClose();
        }}
      >
        <DialogPortal>
          <DialogOverlay className={dialogZ} />
          <MotionDialogContent
            initial={false}
            animate={shellAnimate}
            transition={SHELL_SPRING}
            style={{ left: "50%", top: "50%", x: "-50%", y: "-50%" }}
            className={cn(
              dialogZ,
              // Pixel size + radius owned by motion spring (scale-from-center both ways)
              "fixed flex max-h-none max-w-none flex-col gap-0 overflow-hidden border border-border/60 bg-background p-0 shadow-2xl outline-none",
              "duration-0 data-[state=open]:animate-none data-[state=closed]:animate-none",
            )}
            onPointerDownOutside={(event) => event.preventDefault()}
            onInteractOutside={(event) => event.preventDefault()}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              focusContainer();
            }}
          >
            <DialogGuideBlocker />
            <DialogTitle className="sr-only">
              {headerTitle}: {file.name} — {entityTypeLabel} {entityRecord.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              View and work on {file.name} for {entityRecord.name}.
            </DialogDescription>
            <TooltipProvider delayDuration={200}>
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <header className="shrink-0 px-5 pt-5 sm:px-7">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                      <h1 className="shrink-0 text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
                        {headerTitle}
                      </h1>
                      <Badge
                        variant="outline"
                        size="md"
                        className="min-w-0 max-w-full shrink justify-start overflow-hidden border-border/70 bg-muted text-sm text-foreground/80"
                        title={`${entityTypeLabel}: ${entityRecord.name}`}
                      >
                        <span className="min-w-0 truncate">
                          {entityTypeLabel} · {entityRecord.name}
                        </span>
                      </Badge>
                    </div>
                    {!isAnnotating ? (
                      <button
                        type="button"
                        className={cn(SURFACE_ICON_BTN_CLS, "inline-flex shrink-0 items-center justify-center")}
                        onClick={() => setOpen(false)}
                        aria-label="Close file detail"
                      >
                        <X className="size-4" />
                      </button>
                    ) : null}
                  </div>
                </header>

                <div className="flex min-h-0 flex-1 items-stretch gap-5 overflow-hidden overscroll-contain px-5 pb-5 pt-2 sm:px-7">
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    {/*
                      Media stage is full column width — image can cover the card edge-to-edge.
                      Chrome inset matches FileDetailRail shell padding (p-5).
                      Focus target for keyboard nav — no visible outline (tabIndex -1 container).
                    */}
                    <div
                      className="group relative min-h-0 w-full flex-1 outline-none focus:outline-none focus-visible:outline-none"
                      {...focusContainerProps}
                    >
                      {imageFile && isAnnotating && project ? (
                        <ImageAnnotationEditor
                          ref={annotationEditorRef}
                          entityRecord={entityRecord}
                          fileToAnnotate={imageFile}
                          className="absolute inset-0 z-20 overflow-hidden rounded-[2rem]"
                          onClose={() => setIsAnnotating(false)}
                          onAfterImageAnnotate={(fileToAnnotate) => {
                            queryClient.invalidateQueries({ queryKey: fileKeys.all });
                            handleFileOpen({
                              ...fileToAnnotate,
                              isAnnotated: true,
                              url: "",
                              thumbnailUrl: null,
                            });
                          }}
                        />
                      ) : (
                        <ImageViewer />
                      )}

                      {!isAnnotating ? (
                        <div className="pointer-events-none absolute inset-0 z-30 p-5">
                          <div className="relative h-full w-full">
                            <FileCollectionNavButtons
                              filesCount={files.length}
                              hasPrevious={hasPrevious}
                              hasNext={hasNext}
                              onPrevious={handlePrevious}
                              onNext={handleNext}
                              variant="overlay"
                            />
                            <div className="pointer-events-auto absolute right-0 top-0 flex flex-col items-end gap-2">
                              <FileActionsMenu
                                file={file}
                                alwaysVisible
                                triggerClassName={cn(SURFACE_ICON_BTN_CLS, "shadow-md")}
                                onFileDownload={handleFileDownload}
                                onFileDelete={handleDeleteFile}
                                onAnnotate={() => handleAnnotating()}
                                onAnalyze={() => handleOpenAnalyzePanel()}
                                onPromote={() => {
                                  void handleOpenPromotePanel();
                                }}
                                onAdjustPoints={() => handleOpenAdjustPointsPanel()}
                                onSummaryShare={() => handleOpenSummarySharePanel()}
                                onExtract={() => setIsExtractionPanelOpen(true)}
                                onCopy={() => {
                                  void copyImageToClipboard(file);
                                }}
                                shouldShowAdjustPoints={shouldShowAdjustPoints}
                              />
                              <PromoteAiInsightChrome />
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {!isAnnotating ? (
                    <FileDetailRail
                      mode={railMode}
                      analyzeDockRef={analyzeDockRef}
                      onBackToDetails={handleCloseSummarySharePanel}
                    />
                  ) : null}
                </div>
              </div>
            </TooltipProvider>
          </MotionDialogContent>
        </DialogPortal>
      </Dialog>

      {file && isAnalyzeDialogOpen && (
        <AnalyzeFileShell open={isAnalyzeDialogOpen} file={file} entityRecord={entityRecord}>
          {isAnalyzePanelDetached ? (
            <AnalyzeFloatingPanel>
              <AnalyzePanelContent
                onOpenChange={(next) => {
                  if (!next) handleCloseAnalyzePanel();
                }}
                unassignedInsightMode={false}
                insightBoardId={null}
                presentation="panel"
                isDetached
                onToggleDetach={toggleAnalyzePanelDetach}
              />
            </AnalyzeFloatingPanel>
          ) : (
            analyzeDockSlot &&
            createPortal(
              <AnalyzePanelContent
                onOpenChange={(next) => {
                  if (!next) handleCloseAnalyzePanel();
                }}
                unassignedInsightMode={false}
                insightBoardId={null}
                presentation="panel"
                isDetached={false}
                onToggleDetach={toggleAnalyzePanelDetach}
              />,
              analyzeDockSlot,
            )
          )}
        </AnalyzeFileShell>
      )}
    </>
  );
};

export const FileDetailModal = ({
  file,
  navigationFiles,
  open,
  setOpen,
  entityRecord,
  autoOpenPromotePanel,
  initialViewerMode,
}: FileDetailModalProps) => {
  const { swivuAppGrants, project, editorHandler } = useContext(BryntumContext);

  if (!open) {
    return null;
  }

  return (
    <FileDetailModalProvider
      entityRecord={entityRecord}
      file={file}
      navigationFiles={navigationFiles}
      open={open}
      setOpen={setOpen}
      project={project}
      swivuAppGrants={swivuAppGrants}
      editorHandler={editorHandler}
      autoOpenPromotePanel={autoOpenPromotePanel}
      initialViewerMode={initialViewerMode}
    >
      <FileDetailModalContent />
    </FileDetailModalProvider>
  );
};
